import React, { useCallback, useEffect, useState } from "react"
import { FaSearch } from "react-icons/fa"
import { FaBackward } from "react-icons/fa6"
import { IoChevronBack, IoCloseOutline } from "react-icons/io5"
import { useNavigate } from "react-router-dom"
import { BottomSheet } from "react-spring-bottom-sheet"
import "react-spring-bottom-sheet/dist/style.css"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import SolidBtn from "@fuse/sharedComponents/buttons/SolidBtn"
import Modal from "@fuse/sharedComponents/modal/Modal"
import { isEmpty } from "lodash-es"
import { ROUTES } from "src/App"

import { DeviceTypes, DownloadLinkMap } from "@fuse/utils/DeviceDection"

import {
  TDeviceType,
  useDeviceTypeByWidth,
} from "@fuse/customHooks/useDeviceTypeByWidth"

import AvatarGroup from "@fuse/pages/eventPage/components/AvatarGroup"
import { WhichBtnState } from "@fuse/pages/eventPage/controller"

import Lock from "@fuse/assets/images/img-lock.png"
import appStoreDownload from "@fuse/assets/images/People/img-app-store-download.svg"
import googlePlayDownload from "@fuse/assets/images/People/img-google-play-download.svg"

const BuddiesGuestList = ({
  onClose,
  onJoinEvent,
  hasJoined,
  guestList,
  acceptedGuestAvatars,
  isAdmin = false,
  isTicketedEvent,
  myJoinStatus,
}) => {
  const deviceType = useDeviceTypeByWidth()
  const isMobileDevice = deviceType === TDeviceType.MOBILE
  const isAwaiting = myJoinStatus === WhichBtnState.UnderReview
  const canJoinEvent =
    myJoinStatus === WhichBtnState.Invited ||
    myJoinStatus === WhichBtnState.NotInvited

  const [searchText, setSearchText] = useState("")
  const [guests, setGuests] = useState(guestList)
  const navigate = useNavigate()
  const filterGuests = (guests, searchText: string) => {
    if (!searchText || isEmpty(guests)) return guests

    const searchLower = searchText.toLowerCase()
    return guests.filter((guest) =>
      guest?.name?.toLowerCase().includes(searchLower),
    )
  }

  const debounce = <T extends (...args: any[]) => void>(
    func: T,
    wait: number,
  ) => {
    let timeout: ReturnType<typeof setTimeout>

    return (...args: Parameters<T>): void => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func(...args), wait)
    }
  }

  const debouncedFilter = useCallback(
    debounce((searchText: string) => {
      const filteredResults = filterGuests(guestList, searchText)
      setGuests(filteredResults)
    }, 300),
    [guestList],
  )

  useEffect(() => {
    debouncedFilter(searchText)
  }, [searchText, debouncedFilter])

  const handleButtonClick = (url: string) => {
    window.open(url, "_blank")
  }

  const adminEmptyGuestContent = () => {
    return (
      <BlurWrapper
        overlayColorDefault="dark"
        overlayOpacityDefault="40"
        className="flex flex-col items-center sm:rounded-2xl justify-center h-full w-full"
      >
        <div className="bg-[#ffffff1A] sm:rounded-2xl shadow-lg text-center w-full flex flex-col relative h-full">
          <div className="flex flex-col items-center justify-start sm:justify-center px-20 flex-1 gap-4 pt-28 sm:pt-0">
            <p className="text-5xl">🧐</p>
            <span className="text-white font-medium text-lg">
              No guests yet
            </span>
            <span className="text-white text-sm font-extralight text-center">
              Share your event link or use the app to invite friends by email or
              SMS.
            </span>
            <div className="flex flex-row gap-5">
              <img
                src={appStoreDownload}
                alt="App Store"
                className="h-10 cursor-pointer"
                onClick={() =>
                  handleButtonClick(DownloadLinkMap[DeviceTypes.IOS])
                }
              />
              <img
                src={googlePlayDownload}
                alt="Google Play"
                className="h-10 cursor-pointer"
                onClick={() =>
                  handleButtonClick(DownloadLinkMap[DeviceTypes.ANDROID])
                }
              />
            </div>
          </div>
        </div>
      </BlurWrapper>
    )
  }

  const renderContent = () => {
    if (isAdmin && isEmpty(guests)) {
      return adminEmptyGuestContent()
    } else {
      return (
        <BlurWrapper
          overlayColorDefault="dark"
          overlayOpacityDefault="40"
          className="flex flex-col items-center sm:rounded-2xl justify-center h-full w-full"
        >
          <div className="bg-[#ffffff1A] sm:rounded-2xl shadow-lg text-center w-full flex flex-col relative h-full">
            <div className="absolute top-3 right-3 hidden sm:block">
              <BlurWrapper
                overlayColorDefault="dark"
                overlayOpacityDefault="40"
                className="h-10 w-10 rounded-full"
              >
                <div
                  onClick={() => {
                    onClose()
                  }}
                  className="pointer-events-auto cursor-pointer focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 rounded-full h-full w-ful items-center justify-center flex"
                >
                  <IoCloseOutline color="white" size={20} />
                </div>
              </BlurWrapper>
            </div>

            {hasJoined ? (
              <div className="flex flex-col items-center px-6 sm:px-8 flex-1 gap-6">
                <div className="mt-8 sm:mt-24 w-full">
                  <BlurWrapper
                    overlayColorDefault="dark"
                    overlayOpacityDefault="40"
                    className="w-full flex flex-row rounded-full items-center px-4 py-2 gap-4"
                  >
                    <FaSearch color="#fff" />
                    <input
                      className="bg-transparent w-full text-white"
                      placeholder="Search"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </BlurWrapper>
                </div>

                {/* ADD HEIGHT TO ENABLE SCROLLING */}
                <div className="w-full flex-1 overflow-y-auto max-h-[45vh] sm:max-h-[50vh] px-4">
                  {guests?.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 py-2">
                      <img
                        src={item?.avatarUrl}
                        className="h-12 w-12 rounded-full"
                      />
                      <span className="flex-1 text-white text-sm font-medium text-left leading-6">
                        {item.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center px-20 flex-1 gap-6 ">
                <img src={Lock} className="h-12" />
                <span className="text-white font-semibold text-2xl">
                  Restricted Access
                </span>
                <span className="text-white text-base ">
                  {isAwaiting
                    ? "You can only see the guests once you are approved by the host"
                    : "You can only see guests once you RSVP to the event"}
                </span>
                {canJoinEvent && (
                  <SolidBtn
                    size="lg"
                    variant={"solid"}
                    onClick={onJoinEvent}
                    className="inline-flex text-white opacity-100 px-10 min-w-48 w-full sm:w-auto"
                  >
                    {isTicketedEvent ? "Get tickets" : "Join event"}
                  </SolidBtn>
                )}
              </div>
            )}
          </div>

          {hasJoined && !isAdmin ? (
            <div className="absolute bottom-0 h-[50%] w-full">
              <BlurWrapper
                overlayColorDefault="dark"
                overlayOpacityDefault="40"
                className="flex items-center rounded-2xl justify-center h-full "
              >
                <div className="flex flex-col items-center justify-center px-12 gap-4">
                  <img src={Lock} className="h-12" />
                  <span className="text-white font-medium text-lg">
                    Restricted Access
                  </span>
                  <span className="text-white text-sm font-extralight ">
                    You can only see guests you’re connected with. Feel free to
                    add more people to your network to see new guests ;)
                  </span>
                  <AvatarGroup
                    data={acceptedGuestAvatars}
                    maxVisible={4} // Max avatars visible
                    avatarSize={46} // Custom avatar size
                    // overlap={-15} // Overlap effect
                    borderColor={"#ffffff59"} // Avatar border color
                  />
                  <SolidBtn
                    size="lg"
                    variant={"solid"}
                    onClick={() => {
                      onClose()
                      navigate(ROUTES.PEOPLE)
                    }}
                    className="inline-flex text-white opacity-100 px-10 min-w-48 w-full sm:w-auto text-sm font-thin py-2"
                  >
                    Add more connections
                  </SolidBtn>
                </div>
              </BlurWrapper>
            </div>
          ) : (
            <>
              {isAdmin && (
                <div className="bottom-0 w-full">
                  <BlurWrapper
                    overlayColorDefault="dark"
                    overlayOpacityDefault="40"
                    className="flex items-center sm:rounded-2xl justify-center h-full"
                  >
                    <div className="flex flex-col items-center justify-center p-8 gap-4">
                      <img src={Lock} className="h-12" />
                      <span className="text-white font-medium text-lg">
                        Want Full Access?
                      </span>
                      <span className="text-white text-sm font-extralight text-center">
                        To view the complete guest list or approve guests,
                        please download our app.
                      </span>
                      <div className="flex flex-row gap-5">
                        <img
                          src={appStoreDownload}
                          alt="App Store"
                          className="h-10 cursor-pointer"
                          onClick={() =>
                            handleButtonClick(DownloadLinkMap[DeviceTypes.IOS])
                          }
                        />
                        <img
                          src={googlePlayDownload}
                          alt="Google Play"
                          className="h-10 cursor-pointer"
                          onClick={() =>
                            handleButtonClick(
                              DownloadLinkMap[DeviceTypes.ANDROID],
                            )
                          }
                        />
                      </div>
                    </div>
                  </BlurWrapper>
                </div>
              )}
            </>
          )}
        </BlurWrapper>
      )
    }
  }

  return isMobileDevice ? (
    <BottomSheet
      open={true}
      onDismiss={onClose}
      className="rsbs-theme-dark rounded-2xl"
    >
      <div className="h-[85vh]">{renderContent()}</div>
    </BottomSheet>
  ) : (
    <div
      className="fixed inset-0 h-full flex items-start justify-center bg-black bg-opacity-50 z-[9999] "
      onClick={() => onClose()}
    >
      <div className="flex min-h-screen items-center justify-center">
        <div
          className="h-[90vh] w-96 relative"
          onClick={(e) => e.stopPropagation()}
        >
          {renderContent()}
        </div>
      </div>
    </div>
  )
}

export default BuddiesGuestList
